import { useEffect, useMemo, useState } from 'react';

import { Box, Skeleton, Spinner, Text } from '@ecoinvent/ui';
import { useParams } from 'react-router';

import AppCard from 'components/UI/AppCard';
import useV4LciaMethods from 'hooks/data/datasets/V4/lcia/useV4LciaMethods';
import { useSupplyChain } from 'hooks/data/datasets/V4/supplyChain/useSupplyChain';

import SupplyChainGraph from './SupplyChainGraph';
import DatasetInfoModal from './SupplyChainGraph/DatasetInfoModal';
import { LciaSelection } from './SupplyChainGraph/SupplyChainLciaFilters';

type Props = { mode: 'flows' | 'impact_assessment' };

const SupplyChain = ({ mode }: Props) => {
  const { spold_id } = useParams();
  const {
    nodes,
    edges,
    nodeCounts,
    shownCounts,
    isLoading,
    isExpanding,
    rootNodeId,
    expandedNodeIds,
    expandNode,
    collapseNode,
    expandedOutputNodeIds,
    expandOutputs,
    collapseOutputs,
    collapseAll,
    showMoreInputs,
    showMoreOutputs,
  } = useSupplyChain(spold_id);
  const { data: lciaMethods = [] } = useV4LciaMethods(spold_id ?? '');
  const [lciaSelection, setLciaSelection] = useState<LciaSelection | null>(null);

  const selectedMethodId = useMemo(
    () => lciaMethods.find((m) => m.name === lciaSelection?.method)?.id ?? '',
    [lciaMethods, lciaSelection?.method]
  );

  const [drawerNodeId, setDrawerNodeId] = useState<string | null>(null);
  const [showSlowMessage, setShowSlowMessage] = useState(false);

  useEffect(() => {
    if (!isExpanding) {
      setShowSlowMessage(false);
      return;
    }
    const timer = setTimeout(() => setShowSlowMessage(true), 5000);
    return () => clearTimeout(timer);
  }, [isExpanding]);

  const drawerNode = nodes.find((n) => n.id === drawerNodeId) ?? null;

  return (
    <>
      <AppCard p={5} border="none" boxShadow="none" overflow="hidden">
        <Box position="relative">
          <Skeleton isLoaded={!isLoading}>
            <SupplyChainGraph
              nodes={nodes}
              edges={edges}
              nodeCounts={nodeCounts}
              shownCounts={shownCounts}
              rootNodeId={rootNodeId}
              expandedNodeIds={expandedNodeIds}
              expandedOutputNodeIds={expandedOutputNodeIds}
              mode={mode}
              selectedMethodId={selectedMethodId}
              lciaSelection={lciaSelection}
              onLciaChange={setLciaSelection}
              onExpand={expandNode}
              onCollapse={collapseNode}
              onExpandOutputs={expandOutputs}
              onCollapseOutputs={collapseOutputs}
              onCollapseAll={collapseAll}
              onViewDataset={setDrawerNodeId}
              showMoreInputs={showMoreInputs}
              showMoreOutputs={showMoreOutputs}
            />
          </Skeleton>
          {isExpanding && (
            <Box
              position="absolute"
              inset={0}
              bg="blackAlpha.700"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              zIndex={10}
              borderRadius="md"
            >
              <Spinner size="xl" color="green.400" thickness="4px" speed="0.65s" />
              {showSlowMessage && (
                <Text mt={6} color="white" fontSize="md" fontWeight={700}>
                  This node is taking longer to load. Please wait.
                </Text>
              )}
            </Box>
          )}
        </Box>
      </AppCard>

      <DatasetInfoModal node={drawerNode} onClose={() => setDrawerNodeId(null)} />
    </>
  );
};

export default SupplyChain;
